import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon as CheckCircle,
  ArrowRightIcon as ArrowRight,
  ForwardIcon as SkipForward,
} from "@heroicons/react/24/outline";
import Button from "../ui/Button";
import { Label } from "../ui/label";
import postRegistrationService, {
  PostRegistrationData,
  UserType,
} from "../../services/postRegistrationService";

interface PostRegistrationFormProps {
  onComplete: () => void;
  onSkip: () => void;
}

const PostRegistrationForm: React.FC<PostRegistrationFormProps> = ({
  onComplete,
  onSkip,
}) => {
  const { t } = useTranslation();
  const [userType, setUserType] = useState<UserType | null>(null);
  const [formData, setFormData] = useState<Partial<PostRegistrationData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const userTypes = [
    {
      id: "specialist" as UserType,
      title: t("postRegistration.userType.specialist"),
      description: "Veterinary specialists with specific expertise",
      icon: "🏥",
    },
    {
      id: "general" as UserType,
      title: t("postRegistration.userType.general"),
      description: "General practice veterinarians",
      icon: "👨‍⚕️",
    },
    {
      id: "student" as UserType,
      title: t("postRegistration.userType.student"),
      description: "Veterinary students",
      icon: "🎓",
    },
    {
      id: "enthusiast" as UserType,
      title: t("postRegistration.userType.enthusiast"),
      description: "Pet owners and veterinary enthusiasts",
      icon: "🐾",
    },
  ];

  const handleUserTypeSelect = (type: UserType) => {
    setUserType(type);
    setFormData({ userType: type });
    setErrors({});
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userType) {
      newErrors.userType = t("postRegistration.common.required");
    }

    if (userType === "specialist") {
      if (!formData.workplace?.trim()) {
        newErrors.workplace = t("postRegistration.common.required");
      }
      if (!formData.specialty?.trim()) {
        newErrors.specialty = t("postRegistration.common.required");
      }
      if (!formData.experience?.trim()) {
        newErrors.experience = t("postRegistration.common.required");
      }
    }

    if (userType === "general") {
      if (!formData.workplace?.trim()) {
        newErrors.workplace = t("postRegistration.common.required");
      }
      if (!formData.experience?.trim()) {
        newErrors.experience = t("postRegistration.common.required");
      }
    }

    if (userType === "student") {
      if (!formData.year?.trim()) {
        newErrors.year = t("postRegistration.common.required");
      }
      if (!formData.university?.trim()) {
        newErrors.university = t("postRegistration.common.required");
      }
    }

    if (userType === "enthusiast") {
      if (!formData.motivation?.trim()) {
        newErrors.motivation = t("postRegistration.common.required");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await postRegistrationService.savePostRegistrationData(
        formData as PostRegistrationData
      );
      setIsSuccess(true);
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      console.error("Error saving post-registration data:", error);
      setErrors({ submit: t("postRegistration.error.save_failed") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await postRegistrationService.skipPostRegistration();
      onSkip();
    } catch (error) {
      console.error("Error skipping post-registration:", error);
      setErrors({ submit: t("postRegistration.error.save_failed") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderUserTypeSelection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t("postRegistration.userType.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {t("postRegistration.description")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {userTypes.map((type) => (
          <motion.button
            key={type.id}
            type="button"
            onClick={() => handleUserTypeSelect(type.id)}
            className={`p-6 rounded-lg border-2 transition-all duration-200 hover:shadow-lg ${
              userType === type.id
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center space-x-4">
              <span className="text-3xl">{type.icon}</span>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {type.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {type.description}
                </p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {errors.userType && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-500 text-sm text-center"
        >
          {errors.userType}
        </motion.p>
      )}
    </motion.div>
  );

  const renderSpecialistForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t("postRegistration.userType.specialist")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Tell us about your specialization
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="workplace" className="text-sm font-medium">
            {t("postRegistration.specialist.workplace")}
          </Label>
          <input
            id="workplace"
            type="text"
            value={formData.workplace || ""}
            onChange={(e) => handleInputChange("workplace", e.target.value)}
            placeholder={t("postRegistration.specialist.workplace_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.workplace
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.workplace && (
            <p className="mt-1 text-sm text-red-500">{errors.workplace}</p>
          )}
        </div>

        <div>
          <Label htmlFor="specialty" className="text-sm font-medium">
            {t("postRegistration.specialist.specialty")}
          </Label>
          <input
            id="specialty"
            type="text"
            value={formData.specialty || ""}
            onChange={(e) => handleInputChange("specialty", e.target.value)}
            placeholder={t("postRegistration.specialist.specialty_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.specialty
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.specialty && (
            <p className="mt-1 text-sm text-red-500">{errors.specialty}</p>
          )}
        </div>

        <div>
          <Label htmlFor="experience" className="text-sm font-medium">
            {t("postRegistration.specialist.experience")}
          </Label>
          <input
            id="experience"
            type="text"
            value={formData.experience || ""}
            onChange={(e) => handleInputChange("experience", e.target.value)}
            placeholder={t(
              "postRegistration.specialist.experience_placeholder"
            )}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.experience
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.experience && (
            <p className="mt-1 text-sm text-red-500">{errors.experience}</p>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderGeneralForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t("postRegistration.userType.general")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Tell us about your practice
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="workplace" className="text-sm font-medium">
            {t("postRegistration.general.workplace")}
          </Label>
          <input
            id="workplace"
            type="text"
            value={formData.workplace || ""}
            onChange={(e) => handleInputChange("workplace", e.target.value)}
            placeholder={t("postRegistration.general.workplace_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.workplace
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.workplace && (
            <p className="mt-1 text-sm text-red-500">{errors.workplace}</p>
          )}
        </div>

        <div>
          <Label htmlFor="experience" className="text-sm font-medium">
            {t("postRegistration.general.experience")}
          </Label>
          <input
            id="experience"
            type="text"
            value={formData.experience || ""}
            onChange={(e) => handleInputChange("experience", e.target.value)}
            placeholder={t("postRegistration.general.experience_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.experience
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.experience && (
            <p className="mt-1 text-sm text-red-500">{errors.experience}</p>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderStudentForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t("postRegistration.userType.student")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Tell us about your studies
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="year" className="text-sm font-medium">
            {t("postRegistration.student.year")}
          </Label>
          <input
            id="year"
            type="text"
            value={formData.year || ""}
            onChange={(e) => handleInputChange("year", e.target.value)}
            placeholder={t("postRegistration.student.year_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.year
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.year && (
            <p className="mt-1 text-sm text-red-500">{errors.year}</p>
          )}
        </div>

        <div>
          <Label htmlFor="university" className="text-sm font-medium">
            {t("postRegistration.student.university")}
          </Label>
          <input
            id="university"
            type="text"
            value={formData.university || ""}
            onChange={(e) => handleInputChange("university", e.target.value)}
            placeholder={t("postRegistration.student.university_placeholder")}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.university
                ? "border-red-300"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          {errors.university && (
            <p className="mt-1 text-sm text-red-500">{errors.university}</p>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderEnthusiastForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t("postRegistration.userType.enthusiast")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Tell us what motivates you
        </p>
      </div>

      <div>
        <Label htmlFor="motivation" className="text-sm font-medium">
          {t("postRegistration.enthusiast.motivation")}
        </Label>
        <textarea
          id="motivation"
          rows={4}
          value={formData.motivation || ""}
          onChange={(e) => handleInputChange("motivation", e.target.value)}
          placeholder={t("postRegistration.enthusiast.motivation_placeholder")}
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.motivation
              ? "border-red-300"
              : "border-gray-300 dark:border-gray-600"
          }`}
        />
        {errors.motivation && (
          <p className="mt-1 text-sm text-red-500">{errors.motivation}</p>
        )}
      </div>
    </motion.div>
  );

  const renderForm = () => {
    switch (userType) {
      case "specialist":
        return renderSpecialistForm();
      case "general":
        return renderGeneralForm();
      case "student":
        return renderStudentForm();
      case "enthusiast":
        return renderEnthusiastForm();
      default:
        return renderUserTypeSelection();
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6"
      >
        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("postRegistration.success.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {t("postRegistration.success.message")}
        </p>
      </motion.div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t("postRegistration.title")}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {t("postRegistration.subtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <AnimatePresence mode="wait">{renderForm()}</AnimatePresence>

          {errors.submit && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <p className="text-red-500 text-sm">{errors.submit}</p>
            </motion.div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {userType && (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t("postRegistration.common.submitting")}</span>
                  </>
                ) : (
                  <>
                    <span>{t("postRegistration.common.submit")}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}

            <Button
              type="button"
              onClick={handleSkip}
              disabled={isSubmitting}
              variant="outline"
              className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <SkipForward className="w-4 h-4" />
              <span>{t("postRegistration.common.skip")}</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostRegistrationForm;
